<script lang="ts">
    import { setContext } from "svelte";
    import { writable } from "svelte/store";
    import { CellState, type Puzzle } from "@lib/types";
    import Grid from "./Grid.svelte";

    interface Props {
        puzzle: Puzzle;
        kings?: number[] | null;
        kingOrderIndices?: number[] | null;
        markerRates?: number[] | null;
        heatmap?: number[] | null;
        markers?: number[] | null;
    }

    let { puzzle, kings = null, kingOrderIndices = null, markerRates = null, heatmap = null, markers = null }: Props = $props();

    const placements = writable<Array<CellState>>(
        Array(puzzle.size ** 2).fill(CellState.Empty) as Array<CellState>,
    );

    const heat = writable<number[] | null>(null);
    const kingOrder = writable<number[] | null>(null);
    const markerRate = writable<number[] | null>(null);

    setContext("puzzle", puzzle);
    setContext("placements", placements);
    setContext("snapshots", writable([]));
    setContext("hintCells", writable([]));
    setContext("heatmap", heat);
    setContext("kingOrder", kingOrder);
    setContext("markerRate", markerRate);

    $effect(() => {
        const cellCount = puzzle.size ** 2;
        const next = Array(cellCount).fill(CellState.Empty) as Array<CellState>;

        if (markers) {
            for (const idx of markers) {
                if (typeof idx === "number" && idx >= 0 && idx < cellCount) {
                    next[idx] = CellState.X;
                }
            }
        }

        if (kings) {
            for (const idx of kings) {
                if (typeof idx === "number" && idx >= 0 && idx < cellCount) {
                    next[idx] = CellState.Star;
                }
            }
        }

        const order = Array(cellCount).fill(0) as number[];
        const orderSrc = (kingOrderIndices && kingOrderIndices.length ? kingOrderIndices : kings) ?? null;
        if (orderSrc) {
            let n = 1;
            for (const idx of orderSrc) {
                if (typeof idx === "number" && idx >= 0 && idx < cellCount) {
                    order[idx] = n;
                    n += 1;
                }
            }
        }
        placements.set(next);
        kingOrder.set(order);
    });

    $effect(() => {
        heat.set(heatmap);
    });

    $effect(() => {
        markerRate.set(markerRates);
    });
</script>

<div class="preview">
    <Grid size={puzzle.size} readonly={true} cloak={false} />
</div>

<style>
    .preview {
        width: 100%;
        max-width: 26rem;
        margin: 0.5rem auto;
        pointer-events: auto;
        cursor: default;
        user-select: none;
    }

    .preview :global(.puzzle-grid) {
        width: 100%;
    }
</style>
